import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  useLazyGetMockTestDataQuery,
  useLazyGetPracticeTestDataQuery,
  useSubmitTestAnswersMutation,
  useLazyGetTestAttemptResultsQuery,
  useUpdateTestAttemptMutation,
} from "../mockTestSlice";
import { useLazyGetBundleTestDataQuery } from "../../bundles/bundleSlice";
import { showNotification } from "../../common/headerSlice";
import ReadingSection from "./ReadingSection";
import TestResultsPopup from "./TestResultsPopup";
import {
  InstructionWrapper,
  useInstructions,
} from "../../../components/Instructions";
import { useTestNavigation } from "../utils/testNavigation";

export default function ReadingPage({ isBundle = false }) {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get navigation utilities for test type
  const { baseRoute, backRoute, getTestUrl, testType } = useTestNavigation();

  // --- Instruction system integration ---
  const {
    instructionState,
    showInstructionModal,
    hideInstructionModal,
    showConfirmationDialog,
    hideConfirmationDialog,
    showUnattemptedDialog,
    hideUnattemptedDialog,
    showTimeExpiryDialog,
    hideTimeExpiryDialog,
  } = useInstructions();

  // Show Reading instructions at section start
  const [showInitialInstructions, setShowInitialInstructions] = useState(true);
  const [instructionsViewed, setInstructionsViewed] = useState(false);
  const [pendingApiCall, setPendingApiCall] = useState(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [wasInstructionsVisible, setWasInstructionsVisible] = useState(false);

  // Check for essential state and redirect if missing (browser refresh)
  const studentAttemptId =
    location.state?.studentAttemptId ||
    location.state?.mockTest?.studentAttemptId;
  useEffect(() => {
    if (!studentAttemptId || !location.state) {
      console.log(
        "Essential state missing, redirecting to main mock test screen"
      );
      navigate(getTestUrl(testId), { replace: true });
    }
  }, [studentAttemptId, location.state, navigate, testId, getTestUrl]);

  // Flag to prevent multiple restorations
  // (No longer needed with simplified state management)

  // --- State declarations ---
  const [answers, setAnswers] = useState({}); // Single answers object like listening module
  const [currentPart, setCurrentPart] = useState("A");
  const [currentExtractIndex, setCurrentExtractIndex] = useState(0);
  const [readingStudentAnswerId, setReadingStudentAnswerId] = useState("");
  const [localReadingData, setLocalReadingData] = useState(null);
  const [instructionsShownForPart, setInstructionsShownForPart] = useState(
    new Set(["A"])
  ); // Track which parts have shown instructions

  // --- Notes state (persistent across parts/extracts) ---
  const [notes, setNotes] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  // --- Unattempted questions dialog state ---
  const [pendingAutoNext, setPendingAutoNext] = useState(false);
  const [unattemptedCount, setUnattemptedCount] = useState(0);

  // ...other imports...
  const simulationStep = location.state?.simulationStep; // NEW: just like in ListeningPage
  const isSimulationMode = !location.state?.isPracticeMode && !!simulationStep; // NEW
  const isPracticeMode = location.state?.isPracticeMode;

  // Practice mode timer management - use API response timer if available
  const timerMinutes = location.state?.timerMinutes;
  const selectedSections = location.state?.selectedSections;
  const sectionOrder = location.state?.sectionOrder || [];
  const currentSectionIndex = location.state?.currentSectionIndex || 0;
  const apiStartTime = location.state?.mockTest?.testStartTime;
  const apiEndTime = location.state?.mockTest?.testEndDate;
  const testAttemptId = location.state?.mockTest?.studentAttemptId;

  // Initialize timer states to null - will be set by useEffect based on API response
  const [practiceTimerStartTime, setPracticeTimerStartTime] = useState(null);
  const [practiceTimerEndTime, setPracticeTimerEndTime] = useState(null);

  // Results state
  const [testResults, setTestResults] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);

  // Listen for end time adjustments from ReadingSection
  useEffect(() => {
    const handleEndTimeAdjustment = (event) => {
      const {
        adjustedEndTime,
        timeSpentOnInstructions,
        totalInstructionTime,
        isExtractChange,
      } = event.detail;
      console.log("Received end time adjustment:", {
        adjustedEndTime,
        timeSpentOnInstructions,
        totalInstructionTime,
        isExtractChange,
      });

      // Update end time with the adjusted value
      setPracticeTimerEndTime(adjustedEndTime);

      console.log(
        `Timer adjusted to compensate for ${timeSpentOnInstructions} seconds spent on instructions`
      );
      console.log(
        `Total time spent on instructions so far: ${totalInstructionTime} seconds`
      );

      // For extract changes, we need to ensure we have enough compensation (at least 10 seconds)
      if (isExtractChange) {
        console.log(
          "This adjustment was for an extract change - ensuring minimum 10 second compensation"
        );
      }
    };

    window.addEventListener("endTimeAdjusted", handleEndTimeAdjustment);
    return () =>
      window.removeEventListener("endTimeAdjusted", handleEndTimeAdjustment);
  }, [isPracticeMode, testAttemptId]);

  // Initialize practice timer only after instructions are closed
  useEffect(() => {
    // Only initialize timer if instructions have been viewed
    if (!instructionsViewed) {
      console.log(
        "Waiting for instructions to close before initializing timer"
      );
      return;
    }

    const isPracticeModeLocal = location.state?.isPracticeMode;
    console.log(
      "ReadingPage timer initialization (after instructions closed):",
      {
        isPracticeModeLocal,
        timerMinutes,
        practiceTimerStartTime,
        practiceTimerEndTime,
        apiStartTime,
        apiEndTime,
      }
    );

    // Priority in practice mode: 1. User-selected timerMinutes, 2. API timer as fallback
    if (isPracticeModeLocal && timerMinutes && !practiceTimerStartTime) {
      // Create new timer from user-selected minutes - this takes priority in practice mode
      const now = new Date();
      const endTime = new Date(now.getTime() + timerMinutes * 60 * 1000);
      setPracticeTimerStartTime(now.toISOString());
      setPracticeTimerEndTime(endTime.toISOString());
      console.log(
        "Creating new practice timer from user selection after instructions closed:",
        {
          startTime: now.toISOString(),
          endTime: endTime.toISOString(),
          userSelectedMinutes: timerMinutes,
        }
      );
    } else if (apiStartTime && apiEndTime && !practiceTimerStartTime) {
      // Use API timer as fallback if no user timer
      const now = new Date();
      const adjustedStartTime = now.toISOString();
      const durationMs =
        new Date(apiEndTime).getTime() - new Date(apiStartTime).getTime();
      const adjustedEndTime = new Date(
        now.getTime() + durationMs
      ).toISOString();

      setPracticeTimerStartTime(adjustedStartTime);
      setPracticeTimerEndTime(adjustedEndTime);
      console.log(
        "Using adjusted API timer as fallback to account for instructions:",
        {
          originalStartTime: apiStartTime,
          originalEndTime: apiEndTime,
          adjustedStartTime,
          adjustedEndTime,
        }
      );
    }
  }, [
    location.state?.isPracticeMode,
    timerMinutes,
    practiceTimerStartTime,
    practiceTimerEndTime,
    apiStartTime,
    apiEndTime,
    instructionsViewed, // Add this to re-run when instructions close
  ]);

  function extractOetReadingParts(data) {
    // Handles both { data: { readingTest: ... }} and flat { oetReadingPart: ... }
    if (!data) return [];
    if (data?.data?.readingTest?.oetReadingPart)
      return data.data.readingTest.oetReadingPart;
    if (data?.oetReadingPart) return data.oetReadingPart;
    return [];
  }

  // API hooks for both simulation and practice modes
  const [triggerGetMockTestData, { data: testData, isLoading, error }] =
    useLazyGetMockTestDataQuery();
  // Use bundle API when isBundle is true, otherwise use mock test API
  const [
    triggerGetMockPracticeData,
    {
      data: mockPracticeTestData,
      isLoading: mockPracticeLoading,
      error: mockPracticeError,
    },
  ] = useLazyGetPracticeTestDataQuery();
  const [
    triggerGetBundleData,
    {
      data: bundlePracticeTestData,
      isLoading: bundlePracticeLoading,
      error: bundlePracticeError,
    },
  ] = useLazyGetBundleTestDataQuery();

  // Use the appropriate data based on isBundle
  const triggerGetPracticeData = isBundle
    ? triggerGetBundleData
    : triggerGetMockPracticeData;
  const practiceTestData = isBundle
    ? bundlePracticeTestData
    : mockPracticeTestData;
  const practiceLoading = isBundle
    ? bundlePracticeLoading
    : mockPracticeLoading;
  const practiceError = isBundle ? bundlePracticeError : mockPracticeError;

  const [submitTestAnswers] = useSubmitTestAnswersMutation();

  // Results API
  const [getTestAttemptResults] = useLazyGetTestAttemptResultsQuery();
  const [updateTestAttempt] = useUpdateTestAttemptMutation();

  // Show Reading Part instructions first, before loading data
  const handleShowInstructions = useCallback(
    (part, extractIndex = null) => {
      const mode = location.state?.isPracticeMode ? "practice" : "simulation";
      const instructionType = part === "A" ? "A" : "BC";
      console.log(
        `Showing instructions for Reading Part ${instructionType} in ${mode} mode`
      );
      showInstructionModal("reading", instructionType, mode);
      setInstructionsViewed(false);
    },
    [location.state?.isPracticeMode, showInstructionModal]
  );

  // Track instruction visibility changes
  useEffect(() => {
    if (instructionState.showInstructions) {
      console.log("Instructions became visible");
      setWasInstructionsVisible(true);
    } else if (wasInstructionsVisible && !instructionState.showInstructions) {
      console.log("Instructions closed, executing pending actions");

      // Record the time when instructions closed - important for timer compensation
      const instructionsClosedTime = Date.now();
      console.log(
        `Instructions closed at: ${new Date(
          instructionsClosedTime
        ).toISOString()}`
      );

      // Execute the pending API call if exists
      if (pendingApiCall) {
        console.log("Executing pending API call:", pendingApiCall);

        if (
          pendingApiCall.type === "practice" ||
          pendingApiCall.type === "simulation"
        ) {
          // Use practice data API for both practice and simulation modes
          triggerGetPracticeData(pendingApiCall.params);
        } else if (pendingApiCall.type === "navigation") {
          // Handle navigation after instructions are closed
          console.log(
            "Navigating after instructions closed:",
            pendingApiCall.params
          );
          navigate(pendingApiCall.params.path, {
            state: pendingApiCall.params.state,
          });
        }

        // Clear the pending API call
        setPendingApiCall(null);
      }

      // Ensure the timer is properly compensated for instruction time
      if (practiceTimerEndTime) {
        console.log(
          "Timer end time after instructions closed:",
          practiceTimerEndTime
        );
      }

      // Mark instructions as viewed - this will trigger timer initialization
      console.log(
        "Marking instructions as viewed, timer will now initialize/resume"
      );
      setInstructionsViewed(true);
      setWasInstructionsVisible(false);
    }
  }, [
    instructionState.showInstructions,
    wasInstructionsVisible,
    pendingApiCall,
    triggerGetPracticeData,
    triggerGetMockTestData,
    navigate,
    practiceTimerEndTime,
  ]);

  // Show initial instructions when component first loads
  useEffect(() => {
    if (showInitialInstructions && isFirstLoad) {
      handleShowInstructions("A");
      setShowInitialInstructions(false);
      setIsFirstLoad(false);
    }
  }, [showInitialInstructions, isFirstLoad, handleShowInstructions]);

  // Show Part BC instructions when transitioning from Part A to B or B to C
  const [lastPart, setLastPart] = useState(null);
  useEffect(() => {
    if (
      currentPart === "B" &&
      lastPart === "A" &&
      instructionsViewed &&
      !instructionsShownForPart.has("B")
    ) {
      console.log("Showing instructions for Part B (first time)");
      handleShowInstructions("B");
      setInstructionsShownForPart((prev) => new Set([...prev, "B"]));
    } else if (
      currentPart === "C" &&
      lastPart === "B" &&
      instructionsViewed &&
      !instructionsShownForPart.has("C")
    ) {
      console.log("Showing instructions for Part C (first time)");
      handleShowInstructions("C");
      setInstructionsShownForPart((prev) => new Set([...prev, "C"]));
    }
    setLastPart(currentPart);
  }, [
    currentPart,
    lastPart,
    instructionsViewed,
    handleShowInstructions,
    instructionsShownForPart,
  ]);

  // Helper function to determine if this is the final section in practice mode
  const isFinalSection = () => {
    const selectedSections = location.state?.selectedSections;
    if (!selectedSections) return false;

    // Check if reading is actually selected - if not, we shouldn't be here
    if (!selectedSections.reading) return false;

    // Check if this is the last section in practice mode
    // If only reading is selected, or if we've completed reading and no writing is selected
    const hasWriting = selectedSections.writing;

    // If no writing section is selected after reading, this is final
    return !hasWriting;
  };

  // Helper function to show results (works for both practice and simulation mode)
  const showTestResults = async (
    studentAttemptId,
    shouldUpdateAttempt = false
  ) => {
    try {
      console.log(
        "Fetching test results for studentAttemptId:",
        studentAttemptId,
        "shouldUpdateAttempt:",
        shouldUpdateAttempt
      );

      // Mark test attempt as completed if requested (simulation mode)
      if (shouldUpdateAttempt) {
        try {
          console.log("Updating test attempt as completed...");
          await updateTestAttempt({
            studentAttemptId,
            isCompleted: true,
          }).unwrap();
          console.log("Test attempt marked as completed");
        } catch (updateError) {
          console.error("Error updating test attempt:", updateError);
          // Continue to fetch results even if update fails
        }
      }

      const resultsResponse = await getTestAttemptResults(
        studentAttemptId
      ).unwrap();
      console.log("Test results:", resultsResponse);
      setTestResults(resultsResponse);
      setShowResultsPopup(true);
    } catch (resultsError) {
      console.error("Error fetching test attempt results:", resultsError);
      // Show user-friendly error message
      dispatch(
        showNotification({
          message: "Error fetching results. Please try again.",
          status: 0,
        })
      );
    }
  };

  // Alias for backward compatibility
  const showPracticeResults = (studentAttemptId) =>
    showTestResults(studentAttemptId, false);

  // Determine which loading states and errors to use based on practice mode
  const currentLoading = isPracticeMode ? practiceLoading : isLoading;
  const currentError = isPracticeMode ? practiceError : error;
  useEffect(() => {
    // Only clear answers when changing between different parts, not extracts within same part
    if (isSimulationMode && location.state?.simulationStep) {
      const parts = extractOetReadingParts(localReadingData);
      const readingPartIndex =
        location.state.simulationStep.readingPartIndex || 0;
      console.log(
        "Simulation step detected - readingPartIndex:",
        readingPartIndex,
        "parts:",
        parts?.map((p) => p.part)
      );

      if (parts && parts[readingPartIndex]) {
        const newPart = parts[readingPartIndex].part;
        console.log(
          "Setting currentPart to:",
          newPart,
          "from readingPartIndex:",
          readingPartIndex
        );

        // Keep all answers preserved like listening module - never clear
        console.log(
          `Moving from Part ${currentPart} to Part ${newPart} - preserving all answers`
        );

        setCurrentPart(newPart);
      }
    }
  }, [
    location.state?.currentExtractIndex,
    location.state?.simulationStep,
    isSimulationMode,
    localReadingData,
    currentPart, // Added currentPart to track part changes
  ]);

  // Helper function to schedule API calls after instructions close
  const scheduleApiCall = useCallback((apiParams, type = "practice") => {
    console.log(
      `Scheduling ${type} API call to run after instructions close:`,
      apiParams
    );
    setPendingApiCall({
      type,
      params: apiParams,
    });
  }, []);

  // Fetch API data based on mode and navigation state
  useEffect(() => {
    const practiceDataFromState = location.state?.practiceData;

    // 1. Always set localReadingData from navigation state if present and not already set
    if (practiceDataFromState && isPracticeMode && !localReadingData) {
      setLocalReadingData(practiceDataFromState);

      // Set part and extract as well
      if (practiceDataFromState?.data?.readingTest?.oetReadingPart) {
        const availableParts =
          practiceDataFromState.data.readingTest.oetReadingPart;
        if (availableParts.length > 0) {
          const firstPart = availableParts[0].part;
          setCurrentPart(firstPart);
          setCurrentExtractIndex(0);
          // Keep existing answers - never clear like listening module
        }
      }
      // Note: Don't set currentPart from readingPartsOrder as those are IDs, not letters
      // The currentPart will be set when API response comes back with actual part letter
      return; // Don't continue, data set from navigation
    }

    // 2. Early guard: only proceed if not already loading or already has data
    if (
      (isPracticeMode ? practiceLoading : isLoading) ||
      localReadingData ||
      pendingApiCall
    )
      return;

    // 3. Now run your original API logic
    const studentAttemptId = location.state?.studentAttemptId;

    if (!studentAttemptId) {
      console.error("No studentAttemptId found in navigation state!");
      return;
    }

    // This check is now redundant but for safety (after above guard):
    if (practiceDataFromState && isPracticeMode) {
      return;
    }

    // We now only prepare the API call parameters but don't call APIs directly
    if (isPracticeMode) {
      const readingPartsOrder = location.state?.readingPartsOrder || [];
      const readingQuestionsOrder = location.state?.readingQuestionsOrder || [];
      const currentReadingPartIndex =
        location.state?.currentReadingPartIndex || 0;

      if (currentReadingPartIndex < readingPartsOrder.length) {
        const currentPartId = readingPartsOrder[currentReadingPartIndex];
        const apiParams = {
          studentAttemptId,
          currentSection: "reading",
          readingId: location.state?.readingTestId,
          readingPartIds: [currentPartId],
          readingQuestionIds: readingQuestionsOrder,
        };

        // Only directly call API if instructions already viewed, otherwise schedule
        if (instructionsViewed) {
          console.log(
            "Instructions already viewed, calling API directly:",
            apiParams
          );
          triggerGetPracticeData(apiParams);
        } else {
          scheduleApiCall(apiParams, "practice");
        }
      }
      // If all parts have been loaded, don't make additional API calls
    } else if (simulationStep) {
      // SIMULATION MODE (step by step), just like Listening
      const readingPartIds = simulationStep.readingPartIds || [];
      const readingPartIndex = simulationStep.readingPartIndex || 0;
      const readingTestId = location.state?.readingTestId;
      const currentPartId = readingPartIds[readingPartIndex];

      if (!readingTestId || !currentPartId) return;

      const apiParams = {
        studentAttemptId,
        currentSection: "reading",
        readingId: readingTestId,
        readingPartIds: [currentPartId],
      };

      if (instructionsViewed) {
        console.log(
          "Instructions already viewed, calling API directly:",
          apiParams
        );
        triggerGetPracticeData(apiParams);
      } else {
        scheduleApiCall(apiParams, "practice");
      }
    } else {
      // Fallback to practice mode API for reading section
      const readingTestId = location.state?.readingTestId;
      // Get readingPartIds from location state, simulationStep, or mockTest
      const readingPartIds =
        location.state?.readingPartIds ||
        location.state?.simulationStep?.readingPartIds ||
        location.state?.mockTest?.oetTest?.readingPartIds ||
        [];

      if (!readingTestId || readingPartIds.length === 0) return;

      // Get the current part index (default to 0 for the first part)
      const currentPartIndex =
        location.state?.simulationStep?.readingPartIndex || 0;
      const currentPartId = readingPartIds[currentPartIndex];

      if (!currentPartId) return;

      const apiParams = {
        studentAttemptId,
        currentSection: "reading",
        readingId: readingTestId,
        readingPartIds: [currentPartId],
      };

      if (instructionsViewed) {
        console.log(
          "Instructions already viewed, calling practice API directly:",
          apiParams
        );
        triggerGetPracticeData(apiParams);
      } else {
        scheduleApiCall(apiParams, "practice");
      }
    }
  }, [
    testId,
    location.state?.studentAttemptId,
    location.state?.practiceData,
    location.state?.currentReadingPartIndex,
    location.state?.isPracticeMode,
    location.state?.readingTestId,
    location.state?.readingPartIds,
    location.state?.readingQuestionIds,
    location.state?.readingPartsOrder,
    location.state?.readingQuestionsOrder,
    currentPart,
    triggerGetMockTestData,
    triggerGetPracticeData,
    isPracticeMode,
    localReadingData,
    practiceLoading,
    isLoading,
    simulationStep,
    instructionsViewed,
    pendingApiCall,
    scheduleApiCall,
  ]);

  // Update localReadingData when API data arrives
  // Update localReadingData when API data arrives
  // Track which part group the current timer belongs to (A or BC)
  const [currentTimerPartGroup, setCurrentTimerPartGroup] = useState(null);

  useEffect(() => {
    if (practiceTestData || testData) {
      const apiData = practiceTestData || testData;
      setLocalReadingData(apiData);

      // Determine the current part group: A is separate, B and C share a timer
      const newPartGroup = currentPart === "A" ? "A" : "BC";

      // Set timer directly from API startTime/endTime
      // - Part A gets its own timer from API
      // - Part B/C share a timer from API (same startTime/endTime for both)
      // Only set timer when switching to a NEW part group to avoid resetting within same group (B->C)
      const shouldSetTimerFromApi =
        isPracticeMode &&
        apiData?.data?.startTime &&
        apiData?.data?.endTime &&
        !timerMinutes && // Only use API timer if user hasn't selected custom timer
        currentTimerPartGroup !== newPartGroup; // Only set when part group changes

      if (shouldSetTimerFromApi) {
        // Use API startTime/endTime directly
        setPracticeTimerStartTime(apiData.data.startTime);
        setPracticeTimerEndTime(apiData.data.endTime);
        setCurrentTimerPartGroup(newPartGroup);

        console.log(`Timer set from API for part group ${newPartGroup}:`, {
          startTime: apiData.data.startTime,
          endTime: apiData.data.endTime,
        });
      }

      // ---- ADD THIS LOGIC ----
      if (apiData?.data?.readingTest?.oetReadingPart) {
        const availableParts = apiData.data.readingTest.oetReadingPart;
        // If currentPart is not a valid letter (A, B, C) or doesn't exist in available parts,
        // set it to the first available part's letter
        const validPartLetters = ["A", "B", "C"];
        const isValidPartLetter = validPartLetters.includes(currentPart);
        const partExistsInData = availableParts.some(
          (p) => p.part === currentPart
        );

        if (!isValidPartLetter || !partExistsInData) {
          console.log(
            "Setting currentPart from API response:",
            availableParts[0].part,
            "Previous value was:",
            currentPart
          );
          setCurrentPart(availableParts[0].part);
          setCurrentExtractIndex(0);
          // Keep existing answers - never clear like listening module
        }
      }
    }
  }, [practiceTestData, testData, currentPart, isPracticeMode]);

  const handleAnswerChange = (questionId, value) => {
    const mode = isSimulationMode ? "SIMULATION" : "PRACTICE";
    console.log(
      `[${mode}] Answer changed for question ${questionId}: ${value} in Part ${currentPart}`
    );

    // Simple answer update like listening module
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    console.log(`[${mode}] Updated answers:`, {
      ...answers,
      [questionId]: value,
    });
  };

  // Handler for adding notes - persists across parts/extracts
  const handleAddNote = (noteData) => {
    const newNote = {
      selectedText: noteData.selectedText,
      userNote: noteData.userNote,
      timestamp: new Date().toLocaleTimeString(),
      id: Date.now(),
      part: currentPart, // Track which part the note was created in
      extractName: `Part ${currentPart}${
        currentExtractIndex > 0 ? ` Extract ${currentExtractIndex + 1}` : ""
      }`,
    };
    setNotes((prev) => [...prev, newNote]);
  };

  // Helper to get all question IDs for the current reading part
  const getAllQuestionIdsForCurrentPart = () => {
    const parts = extractOetReadingParts(localReadingData);
    const currentPartData = parts.find((p) => p.part === currentPart);

    if (!currentPartData || !currentPartData.questions) {
      return [];
    }

    let questionIds = [];

    if (currentPart === "A") {
      // Part A: All questions in the part
      questionIds = currentPartData.questions.map((q) => String(q.questionId));
    } else if (currentPart === "B") {
      // Part B: Submit ALL questions from Part B (not just current extract)
      // This ensures all answered questions (21-26) are included in payload
      questionIds = currentPartData.questions.map((q) => String(q.questionId));
    } else if (currentPart === "C") {
      // Part C: Questions for current extract
      const allQuestions = currentPartData.questions || [];
      const currentExtractIdx = simulationExtractIndex;

      if (isPracticeMode) {
        // In practice mode, group questions by extract and get current extract's questions
        const questionsByExtract = {};
        allQuestions.forEach((q) => {
          const extractId = q.readingTextId;
          if (!questionsByExtract[extractId]) {
            questionsByExtract[extractId] = [];
          }
          questionsByExtract[extractId].push(q);
        });

        const uniqueExtractIds = Object.keys(questionsByExtract);
        const currentExtractId = uniqueExtractIds[currentExtractIdx];

        if (currentExtractId && questionsByExtract[currentExtractId]) {
          questionIds = questionsByExtract[currentExtractId].map((q) =>
            String(q.questionId)
          );
        }
      } else {
        // Simulation mode: use original logic
        const uniqueExtractIds = [
          ...new Set(allQuestions.map((q) => q.readingTextId)),
        ];
        const currentExtractId = uniqueExtractIds[currentExtractIdx];

        if (currentExtractId) {
          const extractQuestions = allQuestions.filter(
            (q) => q.readingTextId === currentExtractId
          );
          questionIds = extractQuestions.map((q) => String(q.questionId));
        }
      }
    }

    return questionIds;
  };
  // Add in ReadingPage.jsx
  const handleJumpToExtract = useCallback(
    (part, extractIndex) => {
      if (isPracticeMode) {
        // Practice: local state drives the view
        setCurrentPart(part);
        setCurrentExtractIndex(extractIndex);
        return;
      }
      // Simulation: navigate with state so the loader fetches the right extract
      navigate(`${baseRoute}/${testId}/reading`, {
        state: {
          ...location.state,
          simulationStep: location.state?.simulationStep,
          currentExtractIndex: extractIndex,
          practiceData: null,
        },
      });
    },
    [isPracticeMode, navigate, location.state, testId]
  );

  // Helper function to handle part transitions with instructions
  const handlePartTransition = (nextPart) => {
    console.log(`Transitioning to part ${nextPart}`);

    // Just change the part - instructions will be shown automatically by the useEffect
    setCurrentPart(nextPart);
    setCurrentExtractIndex(0);
  };

  const handleAutoNext = async () => {
    await proceedAutoNext();
  };

  // Actual logic to proceed to next part/section
  const proceedAutoNext = async () => {
    console.log("=== HANDLE AUTO NEXT CALLED ===");
    console.log("Current part completed:", currentPart, "answers:", answers);
    console.log("isSimulationMode:", isSimulationMode);
    console.log("isPracticeMode:", isPracticeMode);
    console.log("simulationStep:", location.state?.simulationStep);
    console.log("=== END HANDLE AUTO NEXT HEADER ===");

    const studentAttemptId = location.state?.studentAttemptId;

    // Helper to extract oetReadingPart array safely
    const extractOetReadingParts = (data) =>
      data?.data?.readingTest?.oetReadingPart ?? [];

    try {
      // Always submit answers (even if empty)
      // Get readingTestId from API data if available
      let readingTestId = testId;
      if (localReadingData?.data?.readingTest?._id) {
        readingTestId = localReadingData.data.readingTest._id;
      }

      // Get all question IDs for the current part and build answers array
      const allQuestionIds = getAllQuestionIdsForCurrentPart();
      const submissionData = {
        testId: readingTestId,
        testAttemptId: studentAttemptId,
        answers: allQuestionIds.map((questionId) => {
          const givenAnswer =
            answers[questionId] || answers[String(questionId)] || "";
          return {
            questionId: isNaN(Number(questionId))
              ? questionId
              : Number(questionId),
            givenAnswer: givenAnswer ?? "",
          };
        }),
        ...(readingStudentAnswerId &&
          typeof readingStudentAnswerId === "string" &&
          readingStudentAnswerId.trim() !== "" && {
            studentAnswerId: readingStudentAnswerId,
          }),
        isNextPart: true,
      };

      console.log("Submitting answers:", submissionData);
      const response = await submitTestAnswers(submissionData).unwrap();
      console.log("Submission response:", response);

      // Update studentAnswerId for next section
      const newStudentAnswerId =
        response?.studentAnswerId ||
        response?.data?.studentAnswerId ||
        response?.data?.data?.studentAnswerId;

      if (newStudentAnswerId) {
        setReadingStudentAnswerId(newStudentAnswerId);
      }

      // ----------------- PRACTICE MODE FLOW -----------------
      if (isPracticeMode) {
        const readingPartsOrder = location.state?.readingPartsOrder || [];
        const readingQuestionsOrder =
          location.state?.readingQuestionsOrder || [];
        const currentReadingPartIndex =
          location.state?.currentReadingPartIndex || 0;

        const parts = extractOetReadingParts(localReadingData);
        const currentPartData = parts.find((p) => p.part === currentPart);

        if (currentPart === "B") {
          const totalSelectedQuestions =
            currentPartData?.questions?.length || 1;
          if (currentExtractIndex < totalSelectedQuestions - 1) {
            // Move to next extract in Part B WITHOUT showing instructions
            const nextExtractIndex = currentExtractIndex + 1;
            console.log(
              `Moving to Part B extract ${nextExtractIndex} without instructions`
            );

            // Don't clear answers when moving within same part - keep them
            setCurrentExtractIndex(nextExtractIndex);
            return;
          }
        } else if (currentPart === "C") {
          const allQuestions = currentPartData?.questions || [];
          const uniqueExtractIds = [
            ...new Set(allQuestions.map((q) => q.readingTextId)),
          ];
          if (currentExtractIndex < uniqueExtractIds.length - 1) {
            // Move to next extract in Part C WITHOUT showing instructions
            const nextExtractIndex = currentExtractIndex + 1;
            console.log(
              `Moving to Part C extract ${nextExtractIndex} without instructions`
            );

            // Don't clear answers when moving within same part - keep them
            setCurrentExtractIndex(nextExtractIndex);
            return;
          }
        }

        // All extracts in current part completed, move to next reading part
        const nextReadingPartIndex = currentReadingPartIndex + 1;
        if (nextReadingPartIndex < readingPartsOrder.length) {
          try {
            const nextPartPracticeData = await triggerGetPracticeData({
              studentAttemptId,
              currentSection: "reading",
              readingId: location.state?.readingTestId,
              readingPartIds: [readingPartsOrder[nextReadingPartIndex]],
              readingQuestionIds: readingQuestionsOrder,
            }).unwrap();

            navigate(`${baseRoute}/${testId}/reading`, {
              state: {
                ...location.state,

                currentReadingPartIndex: nextReadingPartIndex,
                practiceData: nextPartPracticeData,
                studentAnswerId: readingStudentAnswerId,
                isPracticeMode: true,
              },
            });
            return;
          } catch (error) {
            console.error("Error fetching next reading part data:", error);
          }
        }

        // All reading parts completed, check if this is the final section or move to next section
        const selectedSections = location.state?.selectedSections;

        if (isFinalSection()) {
          // This is the final section - show results
          await showPracticeResults(studentAttemptId);
        } else if (selectedSections?.writing) {
          navigate(`${baseRoute}/${testId}/writing`, {
            state: {
              ...location.state,
              studentAnswerId: readingStudentAnswerId,
              practiceData: null,
            },
          });
        } else {
          // Fallback - fetch results and show popup
          await showPracticeResults(studentAttemptId);
        }
      }

      // ----------------- SIMULATION MODE FLOW -----------------
      else if (location.state?.simulationStep) {
        const simulationStep = location.state.simulationStep;
        const readingPartIds = simulationStep.readingPartIds || [];
        const readingPartIndex = simulationStep.readingPartIndex || 0;
        const totalReadingParts =
          simulationStep.totalReadingParts || readingPartIds.length;

        const parts = extractOetReadingParts(localReadingData);
        const currentPartData = parts.find((p) => p.part === currentPart);

        // Always use navigation state for extract index if present
        const currentExtractIdx =
          typeof location.state.currentExtractIndex === "number"
            ? location.state.currentExtractIndex
            : currentExtractIndex;

        // Handle extract navigation for Parts B and C (same as practice mode)
        console.log(
          "Simulation mode handleAutoNext - currentPart:",
          currentPart,
          "currentExtractIdx:",
          currentExtractIdx
        );

        if (currentPart === "B") {
          const totalQuestions = currentPartData?.questions?.length || 1;
          console.log(
            "Part B - totalQuestions:",
            totalQuestions,
            "currentExtractIdx:",
            currentExtractIdx
          );

          if (currentExtractIdx < totalQuestions - 1) {
            console.log(
              "Navigating to next extract in Part B:",
              currentExtractIdx + 1
            );
            // Keep all answers preserved - never clear like listening module

            // Navigate directly to next extract WITHOUT showing instructions
            navigate(`${baseRoute}/${testId}/reading`, {
              state: {
                ...location.state,
                simulationStep: { ...simulationStep, readingPartIndex },
                currentExtractIndex: currentExtractIdx + 1,
                studentAnswerId: readingStudentAnswerId,
                practiceData: null,
              },
            });
            return;
          } else {
            console.log("Part B completed, moving to next part");
          }
        } else if (currentPart === "C") {
          const allQuestions = currentPartData?.questions || [];
          const uniqueExtractIds = [
            ...new Set(allQuestions.map((q) => q.readingTextId)),
          ];
          console.log(
            "Part C - uniqueExtractIds:",
            uniqueExtractIds.length,
            "currentExtractIdx:",
            currentExtractIdx
          );

          if (currentExtractIdx < uniqueExtractIds.length - 1) {
            console.log(
              "Navigating to next extract in Part C:",
              currentExtractIdx + 1
            );
            // Keep all answers preserved - never clear like listening module

            // Navigate directly to next extract WITHOUT showing instructions
            navigate(`${baseRoute}/${testId}/reading`, {
              state: {
                ...location.state,
                simulationStep: { ...simulationStep, readingPartIndex },
                currentExtractIndex: currentExtractIdx + 1,
                studentAnswerId: readingStudentAnswerId,
                practiceData: null,
              },
            });
            return;
          } else {
            console.log("Part C completed, moving to next part");
          }
        }

        // All extracts of this part done: move to next part (first extract)
        const nextReadingPartIndex = readingPartIndex + 1;
        console.log(
          "Checking if more reading parts available:",
          nextReadingPartIndex,
          "of",
          totalReadingParts
        );

        if (nextReadingPartIndex < totalReadingParts) {
          console.log("Moving to next reading part:", nextReadingPartIndex);
          setLocalReadingData(null);
          // Keep all answers preserved - never clear like listening module
          navigate(`${baseRoute}/${testId}/reading`, {
            state: {
              ...location.state,
              simulationStep: {
                ...simulationStep,
                readingPartIndex: nextReadingPartIndex,
              },
              currentExtractIndex: 0, // Reset for new part
              studentAnswerId: readingStudentAnswerId,
              practiceData: null,
            },
          });
          return;
        }

        // All reading parts done, move to writing/results
        console.log(
          "All reading parts completed! Checking selectedSections:",
          location.state?.selectedSections
        );
        console.log("isPracticeMode:", location.state?.isPracticeMode);

        const selectedSections = location.state?.selectedSections;
        const isPracticeMode = location.state?.isPracticeMode;

        // In simulation mode, automatically proceed to writing if it exists
        // In practice mode, check selectedSections
        const shouldNavigateToWriting = isPracticeMode
          ? selectedSections?.writing
          : location.state?.mockTest?.oetTest?.writingId; // Check if writing test exists in simulation

        if (shouldNavigateToWriting) {
          console.log("Writing section available, navigating to writing page");
          console.log(
            "Writing test ID:",
            isPracticeMode
              ? "practice mode"
              : location.state?.mockTest?.oetTest?.writingId
          );

          // Update simulation step to indicate reading is completed
          const updatedSimulationStep = {
            ...simulationStep,
            readingCompleted: true,
            currentSection: "writing",
            previousSection: "reading",
          };

          navigate(`${baseRoute}/${testId}/writing`, {
            state: {
              ...location.state,
              studentAnswerId: readingStudentAnswerId,
              simulationStep: updatedSimulationStep,
              practiceData: null,
            },
          });
        } else {
          console.log(
            "No writing section available, checking final section logic"
          );
          console.log(
            "Reason:",
            isPracticeMode
              ? "Writing not selected in practice mode"
              : "No writing test ID in simulation"
          );

          // For practice mode, show results popup. For simulation, also show results properly.
          if (isPracticeMode && isFinalSection()) {
            await showPracticeResults(studentAttemptId);
          } else if (isSimulationMode) {
            // Simulation mode: Mark test as completed and show results popup
            console.log(
              "Simulation mode: Reading is last section, completing test and showing results"
            );
            await showTestResults(studentAttemptId, true); // true = update attempt as completed
          } else {
            // Fallback: Show results popup
            await showTestResults(studentAttemptId, true);
          }
        }
        return;
      }

      // ----------------- OLD FALLBACK (optional) -----------------
      else {
        // Old fallback (can be removed if not needed)
        const parts = extractOetReadingParts(localReadingData);
        const partB = parts.find((p) => p.part === "B");
        const partC = parts.find((p) => p.part === "C");
        const totalPartBExtracts = partB?.oetReadingText?.length || 6;
        const totalPartCExtracts = partC?.oetReadingText?.length || 2;

        if (currentPart === "A") {
          // Transition from A to B - will show instructions via the useEffect
          setCurrentPart("B");
          setCurrentExtractIndex(0);
        } else if (currentPart === "B") {
          if (currentExtractIndex < totalPartBExtracts - 1) {
            // Move to next extract in Part B without instructions
            setCurrentExtractIndex(currentExtractIndex + 1);
            // Don't clear answers when moving within same part
          } else {
            // Transition from B to C - will show instructions via the useEffect
            setCurrentPart("C");
            setCurrentExtractIndex(0);
          }
        } else if (currentPart === "C") {
          if (currentExtractIndex < totalPartCExtracts - 1) {
            // Move to next extract in Part C without instructions
            setCurrentExtractIndex(currentExtractIndex + 1);
          } else {
            navigate(`${baseRoute}/${testId}/writing`, {
              state: {
                ...location.state,
                studentAnswerId: readingStudentAnswerId,
              },
            });
            return;
          }
        }
      }
    } catch (error) {
      console.error("Error in handleAutoNext:", error);
      dispatch(
        showNotification({
          message: "Error submitting answers. Please try again.",
          status: 0,
        })
      );
    }
  };

  // Handler for unattempted dialog confirmation
  const handleUnattemptedContinue = async () => {
    setPendingAutoNext(false);
    hideUnattemptedDialog();
    await proceedAutoNext();
  };

  // At the top of your component (just after state declarations):
  const simulationExtractIndex =
    isSimulationMode && typeof location.state?.currentExtractIndex === "number"
      ? location.state.currentExtractIndex
      : currentExtractIndex;

  console.log("=== EXTRACT INDEX DEBUG ===");
  console.log("isSimulationMode:", isSimulationMode);
  console.log(
    "location.state.currentExtractIndex:",
    location.state?.currentExtractIndex
  );
  console.log("currentExtractIndex (state):", currentExtractIndex);
  console.log("simulationExtractIndex (final):", simulationExtractIndex);
  console.log("=== END EXTRACT INDEX DEBUG ===");

  // Then, in your useCallback:
  const transformReadingData = useCallback(
    (apiData) => {
      const parts = extractOetReadingParts(apiData);

      const extractIdx = simulationExtractIndex; // always use this for index in all modes!

      console.log("transformReadingData called with:", {
        apiDataExists: !!apiData,
        currentPart,
        extractIdx,
        isSimulationMode,
        dataStructure: parts.map((p) => ({
          part: p.part,
          textsCount: p.oetReadingText?.length,
          questionsCount: p.questions?.length,
          questionTypes: p.questions?.map((q) => q.questionType),
          questionIds: p.questions?.map((q) => q.questionId),
        })),
      });

      if (!parts || !Array.isArray(parts) || parts.length === 0) {
        console.log(
          "transformReadingData returning null - no reading parts found"
        );
        return null;
      }

      // ---------- PART A ----------
      if (currentPart === "A") {
        const partA = parts.find((p) => p.part === "A");
        if (!partA) return null;
        return {
          partA: {
            heading: partA.heading,
            readingTexts:
              partA.oetReadingText?.map((text) => ({
                title: text.title,
                paragraphs: text.paragraph ? [text.paragraph] : [],
                imageUrl: text.imageUrl || null,
                tables: text.tables || [],
              })) || [],
            mcqQuestions:
              partA.questions
                ?.filter((q) => q.questionType === "mcq")
                .map((q) => ({
                  ...q,
                  question_id: q.questionId,
                })) || [],
            sentenceCompletionQuestions:
              partA.questions
                ?.filter((q) => q.questionType === "sentencecompletion")
                .map((q) => ({
                  ...q,
                  question_id: q.questionId,
                })) || [],
            noteCompletionQuestions:
              partA.questions
                ?.filter((q) => q.questionType === "notecompletion")
                .map((q) => ({
                  ...q,
                  question_id: q.questionId,
                })) || [],
          },
          partB: { readingTexts: [] },
          partC: { readingTexts: [] },
        };
      }

      // ---------- PART B ----------
      if (currentPart === "B") {
        const partB = parts.find((p) => p.part === "B");
        if (!partB) return null;

        const allExtracts = partB.oetReadingText || [];
        const allQuestions = partB.questions || [];

        // In practice mode, we need to get the correct question for the current extract
        // The extractIdx represents which question we're currently showing from the selected questions
        let currentQuestion = null;
        let currentExtract = null;

        if (isPracticeMode) {
          // In practice mode, the questions are already filtered to only include selected ones
          // So we can directly use extractIdx to get the current question
          currentQuestion = allQuestions[extractIdx];

          // Find the extract that matches this question's readingTextId
          if (currentQuestion) {
            currentExtract = allExtracts.find(
              (extract) => extract._id === currentQuestion.readingTextId
            );
          }

          // Fallback: if no match found, use traditional mapping
          if (!currentExtract) {
            currentExtract = allExtracts[extractIdx];
          }
        } else {
          // In simulation mode, use the original logic
          currentExtract = allExtracts[extractIdx];
          currentQuestion =
            allQuestions.find((q) => q.readingTextId === currentExtract?._id) ||
            allQuestions[extractIdx]; // fallback
        }

        console.log("=== PART B TRANSFORM DEBUG ===");
        console.log("isPracticeMode:", isPracticeMode);
        console.log("extractIdx:", extractIdx);
        console.log("allExtracts.length:", allExtracts.length);
        console.log("allQuestions.length:", allQuestions.length);
        console.log(
          "currentExtract:",
          currentExtract?.title,
          currentExtract?._id
        );
        console.log(
          "currentQuestion:",
          currentQuestion?.questionId,
          currentQuestion?.readingTextId
        );
        console.log("=== END PART B TRANSFORM DEBUG ===");

        return {
          partA: { readingTexts: [] },
          partB: {
            heading: partB.heading,
            readingTexts: [
              {
                title: currentExtract?.title,
                paragraphs: currentExtract?.paragraph
                  ? [currentExtract.paragraph]
                  : [],
                imageUrl: currentExtract?.imageUrl || null,
                tables: currentExtract?.tables || [],
              },
            ],
            mcqQuestions: currentQuestion
              ? [
                  {
                    ...currentQuestion,
                    question_id: currentQuestion.questionId,
                  },
                ]
              : [],
          },
          partC: { readingTexts: [] },
        };
      }

      // ---------- PART C ----------
      if (currentPart === "C") {
        const partC = parts.find((p) => p.part === "C");
        if (!partC) return null;

        const allExtracts = partC.oetReadingText || [];
        const allQuestions = partC.questions || [];

        let currentExtract = null;
        let extractQuestions = [];

        if (isPracticeMode) {
          // In practice mode, questions are already filtered to selected ones
          // Group questions by their readingTextId to find extracts
          const questionsByExtract = {};
          allQuestions.forEach((q) => {
            const extractId = q.readingTextId;
            if (!questionsByExtract[extractId]) {
              questionsByExtract[extractId] = [];
            }
            questionsByExtract[extractId].push(q);
          });

          // Get the unique extract IDs in the order they appear in the selected questions
          const uniqueExtractIds = Object.keys(questionsByExtract);

          // Use extractIdx to determine which extract we're showing
          const currentExtractId = uniqueExtractIds[extractIdx];

          if (currentExtractId) {
            currentExtract = allExtracts.find(
              (extract) => extract._id === currentExtractId
            );
            extractQuestions = questionsByExtract[currentExtractId] || [];
          }

          console.log("=== PART C PRACTICE MODE DEBUG ===");
          console.log("extractIdx:", extractIdx);
          console.log("uniqueExtractIds:", uniqueExtractIds);
          console.log("currentExtractId:", currentExtractId);
          console.log(
            "extractQuestions:",
            extractQuestions.map((q) => q.questionId)
          );
          console.log("=== END PART C PRACTICE MODE DEBUG ===");
        } else {
          // Simulation mode: use the original logic
          const uniqueExtractIds = [
            ...new Set(allQuestions.map((q) => q.readingTextId)),
          ];

          const currentExtractId = uniqueExtractIds[extractIdx];
          currentExtract = allExtracts.find(
            (extract) => extract._id === currentExtractId
          );

          if (currentExtractId) {
            extractQuestions = allQuestions.filter(
              (q) => q.readingTextId === currentExtractId
            );
          }
        }

        // Fallback: if no questions found via readingTextId, use the traditional slicing method
        if (extractQuestions.length === 0) {
          const extractStart = extractIdx * 8;
          const extractEnd = extractStart + 8;
          extractQuestions = allQuestions.slice(extractStart, extractEnd);
          if (extractQuestions.length === 0 && allQuestions.length > 0) {
            extractQuestions = allQuestions;
          }
        }
        if (extractQuestions.length === 0) {
          const extractStart = extractIdx * 8;
          const extractEnd = extractStart + 8;
          extractQuestions = allQuestions.slice(extractStart, extractEnd);
          if (extractQuestions.length === 0 && allQuestions.length > 0) {
            extractQuestions = allQuestions;
          }
        }

        return {
          partA: { readingTexts: [] },
          partB: { readingTexts: [] },
          partC: {
            heading: partC.heading,
            readingTexts: [
              {
                title: currentExtract?.title,
                paragraphs: currentExtract?.paragraph
                  ? [currentExtract.paragraph]
                  : [],
                imageUrl: currentExtract?.imageUrl || null,
                tables: currentExtract?.tables || [],
              },
            ],
            mcqQuestions: extractQuestions.map((q) => ({
              ...q,
              question_id: q.questionId,
            })),
          },
        };
      }

      console.log(
        "transformReadingData returning null - currentPart didn't match any case:",
        currentPart
      );
      return null;
    },
    [
      currentPart,
      simulationExtractIndex, // Only depend on this, not currentExtractIndex!
      isSimulationMode,
      isPracticeMode,
    ]
  );

  const readingData = useMemo(() => {
    // Use practice data from navigation state if available, otherwise use localReadingData
    const dataToTransform = localReadingData || location.state?.practiceData;

    if (!dataToTransform) {
      console.log("ReadingData memo: No data to transform");
      return null;
    }

    console.log(
      "Memo: transforming reading data with currentPart:",
      currentPart,
      "dataSource:",
      localReadingData ? "localReadingData" : "practiceDataFromState"
    );
    return transformReadingData(dataToTransform);
  }, [
    localReadingData,
    location.state?.practiceData,
    currentPart,
    transformReadingData,
  ]);

  const totalExtracts = (() => {
    const parts = extractOetReadingParts(localReadingData);
    const currentPartData = parts.find((p) => p.part === currentPart);

    if (currentPart === "A") {
      return 1;
    } else if (currentPart === "B") {
      // For both modes: number of extracts = number of questions (1:1 mapping)
      return currentPartData?.questions?.length || 1;
    } else if (currentPart === "C") {
      // For both modes: number of unique extracts based on readingTextId
      const allQuestions = currentPartData?.questions || [];
      const uniqueExtractIds = [
        ...new Set(allQuestions.map((q) => q.readingTextId)),
      ];
      return uniqueExtractIds.length || 1;
    }
    return 1;
  })();

  // Check if we should show loader
  const practiceDataFromState = location.state?.practiceData;
  const shouldShowLoader = () => {
    // Don't show loader when instructions are visible - we want to show instructions on blank/blurred screen
    if (instructionState.showInstructions) {
      return false;
    }

    // After instructions close, only show loader if we're loading API data
    const apiLoading = isPracticeMode ? practiceLoading : isLoading;

    // If we don't have data yet and instructions are closed, show loader
    const noDataYet = !localReadingData && !location.state?.practiceData;

    return (apiLoading || noDataYet) && instructionsViewed;
  };

  if (shouldShowLoader()) {
    console.log(
      "ReadingPage - Showing loader. currentLoading:",
      currentLoading,
      "localReadingData:",
      !!localReadingData,
      "isPracticeMode:",
      isPracticeMode,
      "practiceTestData:",
      !!practiceTestData,
      "testData:",
      !!testData,
      "practiceDataFromState:",
      !!practiceDataFromState
    );

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} thickness={5} />
        <Typography sx={{ ml: 2 }}>Loading reading data...</Typography>
      </Box>
    );
  }

  if (currentError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography color="error">
          Error loading reading test: {currentError.message}
        </Typography>
      </Box>
    );
  }

  // Debug logging
  console.log("ReadingPage - Raw API data:", localReadingData);
  console.log(
    "ReadingPage - Practice data from state:",
    location.state?.practiceData
  );
  console.log("ReadingPage - Transformed data:", readingData);
  console.log("ReadingPage - Current part state:", currentPart);
  console.log("ReadingPage - Practice mode:", isPracticeMode);
  console.log(
    "ReadingPage - Current reading part index:",
    location.state?.currentReadingPartIndex
  );
  console.log(
    "ReadingPage - localReadingData structure:",
    extractOetReadingParts(localReadingData)
  );

  const hasAnyData = localReadingData || location.state?.practiceData;
  if (!readingData && !currentLoading && hasAnyData) {
    console.log("ReadingPage - No reading data available after transformation");
    console.log("localReadingData:", localReadingData);
    console.log("practiceDataFromState:", location.state?.practiceData);
    console.log(
      "transformReadingData result:",
      transformReadingData(hasAnyData)
    );
    console.log("currentPart:", currentPart);
    console.log(
      "Available parts in raw data:",
      hasAnyData?.data?.readingTest?.oetReadingPart?.map((p) => p.part)
    );
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography>No reading data available</Typography>
      </Box>
    );
  }

  // Final debug before rendering ReadingSection
  console.log("=== READING FINAL RENDER DEBUG ===");
  console.log("About to render ReadingSection with:");
  console.log("- currentPart:", currentPart);
  console.log("- currentExtractIndex:", currentExtractIndex);
  console.log("- readingData available:", !!readingData);
  console.log("- isPracticeMode:", isPracticeMode);
  console.log("=== END READING FINAL RENDER DEBUG ===");

  // Use part group for key to prevent remounting when moving from B to C
  const partGroupForKey = currentPart === "A" ? "A" : "BC";

  return (
    <>
      {/* Instruction System Wrapper */}
      <InstructionWrapper
        instructionState={instructionState}
        onInstructionNext={hideInstructionModal}
        onInstructionClose={hideInstructionModal}
        onConfirmationConfirm={() => {
          hideConfirmationDialog();
          hideUnattemptedDialog();
        }}
        onConfirmationClose={hideConfirmationDialog}
        onUnattemptedContinue={handleUnattemptedContinue}
        onUnattemptedClose={() => {
          hideUnattemptedDialog();
        }}
        onTimeExpiryContinue={hideTimeExpiryDialog}
        timeRemaining={null}
        unattemptedCount={unattemptedCount}
        nextSection="writing"
      />
      <ReadingSection
        key={`reading-${partGroupForKey}`}
        readingData={readingData}
        rawReadingData={localReadingData} // Pass raw data for navigation
        screen={0}
        instrOpen={
          instructionState.showInstructions &&
          instructionState.currentSection === "reading"
        }
        answers={answers}
        onAnswerChange={handleAnswerChange}
        onAutoNext={handleAutoNext}
        setCurrentPart={setCurrentPart}
        setCurrentExtractIndex={setCurrentExtractIndex}
        setAnswers={setAnswers}
        onJumpToExtract={handleJumpToExtract}
        timer={isPracticeMode && timerMinutes ? timerMinutes : "no-limit"}
        mode={isPracticeMode ? "practice" : "simulation"}
        currentPart={currentPart}
        currentExtractIndex={simulationExtractIndex}
        totalExtracts={totalExtracts}
        instructionsViewed={instructionsViewed}
        startTime={
          practiceTimerStartTime || localReadingData?.data?.startTime || null
        }
        endTime={
          // Don't pass endTime in practice mode with user-selected timer to avoid dual timers
          isPracticeMode && timerMinutes
            ? null
            : practiceTimerEndTime || localReadingData?.data?.endTime || null
        }
        testId={testId}
        // Notes props - persistent across parts/extracts
        notes={notes}
        onAddNote={handleAddNote}
        notesModalOpen={notesModalOpen}
        setNotesModalOpen={setNotesModalOpen}
        onBack={async (prevExtractIndex, part) => {
          console.log("=== READING ONBACK CALLED ===");
          console.log("prevExtractIndex:", prevExtractIndex);
          console.log("part:", part);
          console.log("currentPart:", currentPart);
          console.log("isPracticeMode:", location.state?.isPracticeMode);
          console.log("isSimulationMode:", !location.state?.isPracticeMode);

          // Special case: Going back from Part C extract 1 to Part B
          if (currentPart === "C" && prevExtractIndex === -1) {
            const mode = isSimulationMode ? "SIMULATION" : "PRACTICE";
            console.log(`[${mode}] Going back from Part C to Part B`);

            console.log(
              `[${mode}] Current answers before navigation:`,
              answers
            );

            const studentAttemptId = location.state?.studentAttemptId;
            const isPracticeMode = location.state?.isPracticeMode;

            if (isPracticeMode) {
              // Practice Mode: Need to fetch Part B data
              try {
                const readingPartsOrder =
                  location.state?.readingPartsOrder || [];
                const readingQuestionsOrder =
                  location.state?.readingQuestionsOrder || [];
                const currentReadingPartIndex =
                  location.state?.currentReadingPartIndex || 0;

                console.log("readingPartsOrder:", readingPartsOrder);
                console.log(
                  "currentReadingPartIndex:",
                  currentReadingPartIndex
                );

                // For practice mode, we need to find the Part B index
                // Since we're currently in Part C, Part B should be before it
                // We know that if we're in Part C, Part B must be at currentReadingPartIndex - 1
                let partBIndex = currentReadingPartIndex - 1;

                console.log(
                  "Calculated Part B index based on current Part C index:",
                  partBIndex
                );
                console.log(
                  "readingPartsOrder.length:",
                  readingPartsOrder.length
                );

                if (partBIndex >= 0 && partBIndex < readingPartsOrder.length) {
                  console.log("Fetching Part B data for practice mode");

                  const partBData = await triggerGetPracticeData({
                    studentAttemptId,
                    currentSection: "reading",
                    readingId: location.state?.readingTestId,
                    readingPartIds: [readingPartsOrder[partBIndex]],
                    readingQuestionIds: readingQuestionsOrder,
                  }).unwrap();

                  console.log(
                    "Part B data fetched successfully, navigating..."
                  );

                  // Navigate back to Part B with the fetched data
                  navigate(`${baseRoute}/${testId}/reading`, {
                    state: {
                      ...location.state,
                      currentReadingPartIndex: partBIndex,
                      practiceData: partBData,
                      // Don't reset currentExtractIndex - let it be restored from saved state
                    },
                  });
                  return;
                } else {
                  console.error(
                    "Invalid Part B index:",
                    partBIndex,
                    "readingPartsOrder.length:",
                    readingPartsOrder.length
                  );
                  dispatch(
                    showNotification({
                      message:
                        "Could not navigate to Part B. Please try again.",
                      status: 0,
                    })
                  );
                  return;
                }
              } catch (error) {
                console.error("Error fetching Part B data:", error);
                dispatch(
                  showNotification({
                    message: "Error loading Part B. Please try again.",
                    status: 0,
                  })
                );
                return;
              }
            } else {
              // Simulation Mode: Navigate back to Part B
              const simulationStep = location.state?.simulationStep;
              const readingPartIds = simulationStep?.readingPartIds || [];

              console.log("simulationStep:", simulationStep);
              console.log("readingPartIds:", readingPartIds);
              console.log("currentPart:", currentPart);

              // Determine the correct current reading part index based on the actual current part
              // Standard OET Reading order: Part A (index 0), Part B (index 1), Part C (index 2)
              let currentPartIndex = -1;

              // Map current part letter to index
              if (currentPart === "A") {
                currentPartIndex = 0;
              } else if (currentPart === "B") {
                currentPartIndex = 1;
              } else if (currentPart === "C") {
                currentPartIndex = 2;
              }

              console.log(
                "Determined current part index based on currentPart:",
                currentPartIndex
              );

              // Calculate Part B index (should be 1 for standard A, B, C order)
              let partBIndex = currentPartIndex - 1;

              console.log("Calculated Part B simulation index:", partBIndex);

              if (partBIndex >= 0 && partBIndex < readingPartIds.length) {
                console.log(
                  "[SIMULATION] Navigating back to Part B in simulation mode"
                );

                try {
                  // For simulation mode, use getPracticeData API like practice mode
                  const readingQuestionsOrder =
                    location.state?.readingQuestionsOrder || [];

                  console.log(
                    "[SIMULATION] Fetching Part B data with readingQuestionsOrder:",
                    readingQuestionsOrder
                  );

                  const partBData = await triggerGetPracticeData({
                    studentAttemptId,
                    currentSection: "reading",
                    readingId: location.state?.readingTestId,
                    readingPartIds: [readingPartIds[partBIndex]],
                    readingQuestionIds: readingQuestionsOrder,
                  }).unwrap();

                  console.log(
                    "[SIMULATION] Part B data fetched successfully, navigating..."
                  );

                  navigate(`${baseRoute}/${testId}/reading`, {
                    state: {
                      ...location.state,
                      simulationStep: {
                        ...simulationStep,
                        readingPartIndex: partBIndex,
                      },
                      // Don't reset currentExtractIndex - let it be restored from saved state
                      practiceData: partBData, // Use practiceData like practice mode
                    },
                  });
                  return;
                } catch (error) {
                  console.error(
                    "Error fetching Part B data for simulation:",
                    error
                  );
                  dispatch(
                    showNotification({
                      message: "Error loading Part B. Please try again.",
                      status: 0,
                    })
                  );
                  return;
                }
              } else {
                console.error(
                  "Invalid Part B simulation index:",
                  partBIndex,
                  "readingPartIds.length:",
                  readingPartIds.length
                );
                dispatch(
                  showNotification({
                    message: "Could not navigate to Part B. Please try again.",
                    status: 0,
                  })
                );
                return;
              }
            }
          }

          // Regular back navigation within the same part
          if (
            location.state?.isPracticeMode &&
            (part === "B" || part === "C") &&
            typeof prevExtractIndex === "number" &&
            prevExtractIndex >= 0
          ) {
            // Practice mode: local index - don't clear answers when going back within same part
            console.log(
              `Going back to extract ${prevExtractIndex} within Part ${part}`
            );
            setCurrentExtractIndex(prevExtractIndex);
            // Don't clear answers when navigating within the same part
            return;
          }

          // --- Simulation Mode: must use navigation ---
          if (
            !location.state?.isPracticeMode &&
            (part === "B" || part === "C") &&
            typeof prevExtractIndex === "number" &&
            prevExtractIndex >= 0
          ) {
            // Always navigate with correct simulationStep and index!
            navigate(`${baseRoute}/${testId}/reading`, {
              state: {
                ...location.state,
                currentExtractIndex: prevExtractIndex,
                // If you use simulationStep, keep it as-is:
                simulationStep: location.state?.simulationStep,
                // Optionally clear answers, or keep as needed:
                practiceData: null,
              },
            });
            return;
          }

          // Default: go back to previous section (existing behavior)
          const sectionOrder = location.state?.sectionOrder || [];
          const currentSectionIndex = location.state?.currentSectionIndex || 0;
          if (currentSectionIndex > 0) {
            const previousSection = sectionOrder[currentSectionIndex - 1];
            navigate(`${baseRoute}/${testId}/${previousSection}`, {
              state: {
                ...location.state,
                currentSectionIndex: currentSectionIndex - 1,
              },
            });
          }
        }}
        isLastQuestion={
          // Practice mode check
          (location.state?.isPracticeMode &&
            (() => {
              // In practice mode, check if this is the very last question

              // First, check if reading is the last section in the overall sectionOrder
              const sectionOrder = location.state?.sectionOrder || [];
              const currentSectionIndex =
                location.state?.currentSectionIndex || 0;

              // Check if "reading" is the last section in the order
              const lastSection = sectionOrder[sectionOrder.length - 1];
              const isReadingLastSection = lastSection === "reading";

              console.log("ReadingPage isLastQuestion debug:", {
                isPracticeMode: location.state?.isPracticeMode,
                sectionOrder,
                currentSectionIndex,
                lastSection,
                isReadingLastSection,
                readingPartsOrder: location.state?.readingPartsOrder,
                currentReadingPartIndex:
                  location.state?.currentReadingPartIndex,
                currentPart,
              });

              // If reading is not the last section overall, then this can't be the last question
              if (!isReadingLastSection) return false;

              // Now check if this is the last question within the reading section
              const readingPartsOrder = location.state?.readingPartsOrder || [];
              const currentReadingPartIndex =
                location.state?.currentReadingPartIndex || 0;

              // Check if we're on the last part of reading
              const isLastReadingPart =
                currentReadingPartIndex === readingPartsOrder.length - 1;
              if (!isLastReadingPart) return false;

              // Now check if we're on the last extract/question within this part
              const parts = extractOetReadingParts(localReadingData);
              const currentPartData = parts.find((p) => p.part === currentPart);

              if (!currentPartData) return false;

              if (currentPart === "A") {
                // Part A has only one extract, so if we're on last reading part and Part A, it's the last question
                console.log("ReadingPage: Part A detected as last question");
                return true;
              } else if (currentPart === "B") {
                // Part B: each extract has one question, check if we're on the last extract
                const totalQuestions = currentPartData?.questions?.length || 1;
                const isLastExtract =
                  simulationExtractIndex >= totalQuestions - 1;
                console.log("ReadingPage: Part B last extract check:", {
                  simulationExtractIndex,
                  totalQuestions,
                  isLastExtract,
                });
                return isLastExtract;
              } else if (currentPart === "C") {
                // Part C: multiple extracts, check if we're on the last extract
                const allQuestions = currentPartData?.questions || [];
                const uniqueExtractIds = [
                  ...new Set(allQuestions.map((q) => q.readingTextId)),
                ];
                return simulationExtractIndex >= uniqueExtractIds.length - 1;
              }

              return false;
            })()) ||
          // Simulation mode check - reading is last section when no writing
          (isSimulationMode &&
            (() => {
              // Check if writing is available
              const hasWriting = location.state?.mockTest?.oetTest?.writingId;
              if (hasWriting) return false; // Writing exists, so reading is not last

              // Check if on last reading part
              const simulationStep = location.state?.simulationStep;
              const readingPartIds = simulationStep?.readingPartIds || [];
              const readingPartIndex = simulationStep?.readingPartIndex || 0;
              const isLastReadingPart =
                readingPartIndex === readingPartIds.length - 1;

              if (!isLastReadingPart) return false;

              // Check if on last extract within the part
              const parts = extractOetReadingParts(localReadingData);
              const currentPartData = parts.find((p) => p.part === currentPart);

              if (!currentPartData) return false;

              if (currentPart === "A") {
                return true;
              } else if (currentPart === "B") {
                const totalQuestions = currentPartData?.questions?.length || 1;
                return simulationExtractIndex >= totalQuestions - 1;
              } else if (currentPart === "C") {
                const allQuestions = currentPartData?.questions || [];
                const uniqueExtractIds = [
                  ...new Set(allQuestions.map((q) => q.readingTextId)),
                ];
                return simulationExtractIndex >= uniqueExtractIds.length - 1;
              }

              return false;
            })())
        }
        onFinish={async (fromTimer) => {
          // On timer expiry OR user clicks Finish: submit and move to writing or show results
          const studentAttemptId = location.state?.studentAttemptId;

          // Handle both practice mode and simulation mode
          if (location.state?.isPracticeMode || isSimulationMode) {
            const selectedSections = location.state?.selectedSections;
            const hasWriting = location.state?.isPracticeMode
              ? selectedSections?.writing
              : location.state?.mockTest?.oetTest?.writingId;

            try {
              // Always submit answers (even if empty)
              let readingTestId = testId;
              if (localReadingData?.data?.readingTest?._id) {
                readingTestId = localReadingData.data.readingTest._id;
              }

              // Get all question IDs for the current part and build answers array
              const allQuestionIds = getAllQuestionIdsForCurrentPart();
              const submissionData = {
                testId: readingTestId,
                testAttemptId: studentAttemptId,
                answers: allQuestionIds.map((questionId) => {
                  const givenAnswer =
                    answers[questionId] || answers[String(questionId)] || "";
                  return {
                    questionId: isNaN(Number(questionId))
                      ? questionId
                      : Number(questionId),
                    givenAnswer: givenAnswer ?? "",
                  };
                }),
                ...(readingStudentAnswerId &&
                  typeof readingStudentAnswerId === "string" &&
                  readingStudentAnswerId.trim() !== "" && {
                    studentAnswerId: readingStudentAnswerId,
                  }),
                isNextPart: true,
              };

              console.log(
                "Submitting remaining answers on finish:",
                submissionData
              );
              await submitTestAnswers(submissionData).unwrap();

              // Clear notes when reading section is completed
              setNotes([]);
              setNotesModalOpen(false);

              // Check if writing is available - if so, navigate to writing
              if (hasWriting) {
                console.log(
                  "Reading finished, writing section is available - navigating to writing"
                );

                // Navigate to writing section
                navigate(`${baseRoute}/${testId}/writing`, {
                  state: {
                    ...location.state,
                    currentSectionIndex:
                      (location.state?.currentSectionIndex || 0) + 1,
                    studentAnswerId: readingStudentAnswerId,
                    simulationStep: isSimulationMode
                      ? {
                          ...location.state?.simulationStep,
                          readingCompleted: true,
                          currentSection: "writing",
                          previousSection: "reading",
                        }
                      : undefined,
                    practiceData: null,
                  },
                });
                return; // Exit early - don't show results yet
              }

              // If no writing section - this is the last section
              // Mark test attempt as completed and show results
              console.log(
                "Reading is final section, completing test and showing results"
              );
              await showTestResults(studentAttemptId, true);
            } catch (error) {
              console.error("Error in onFinish:", error);
              // If error, still try to show results
              await showTestResults(studentAttemptId, true);
            }
          }
        }}
      />

      {/* Test Results Popup for Practice Mode */}
      <TestResultsPopup
        open={showResultsPopup}
        onClose={() => setShowResultsPopup(false)}
        resultsData={testResults}
        onContinue={() => navigate(backRoute)}
        attemptId={studentAttemptId}
        mockTestId={testId}
      />
    </>
  );
}
